import { DelayUnit } from '@/services/types/Sequences.types';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const encodeBase64 = (str: string) => {
  const encoder = new TextEncoder(); // Encode to UTF-8
  const encodedData = encoder.encode(str); // Get Uint8Array
  const base64String = btoa(String.fromCharCode(...encodedData)); // Encode to base64
  return base64String;
};

export const formatDateTime = (dateTimeString: string): string => {
  const date = new Date(dateTimeString);
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  };
  return new Intl.DateTimeFormat('en-US', options).format(date).replace(',', ' -');
};

export function formatDateUserReadable(dateString: string): string {
  const date = new Date(dateString);
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  };
  return new Intl.DateTimeFormat('en-US', options).format(date);
}

export const decodeBase64 = (base64Data: string): string => `data:image/jpeg;base64,${base64Data}`;

export function dataURLtoFile(dataUrl: string, filename: string): File | null {
  try {
    const arr = dataUrl.split(',');
    if (arr.length < 2) return null;

    const mimeMatch = arr[0].match(/:(.*?);/);
    if (!mimeMatch || mimeMatch.length < 2) return null;

    const mime = mimeMatch[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], filename, { type: mime });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Error converting data URL to File:', e);
    return null;
  }
}

export const extractAndDecodeMessage = (url: string): string => {
  try {
    const urlObj = new URL(url);
    const encodedMessage = urlObj.searchParams.get('text');
    if (!encodedMessage) return '';
    return decodeURIComponent(encodedMessage);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Error parsing URL:', e);
    return '';
  }
};

export const convertToUnit = (value: number, from: DelayUnit, to: DelayUnit): number => {
  const multipliers: Record<DelayUnit, number> = {
    [DelayUnit.seconds]: 1,
    [DelayUnit.minutes]: 60,
    [DelayUnit.hours]: 3600,
    [DelayUnit.days]: 86400,
  };
  // Convert to seconds, then to target unit
  const valueInSeconds = value * multipliers[from];
  return valueInSeconds / multipliers[to];
};

export const isUrl = (value: string): boolean => {
  const urlPattern = /^(https?:\/\/|www\.|[a-zA-Z0-9-]+\.[a-z]{2,}\/).*$/i;
  return urlPattern.test(value.trim()) || value.includes('instagram.com');
};

export const formatPhoneNumber = (phoneNumber: string | undefined | null): string => {
  if (!phoneNumber) return '';
  // If it's not a number string, return as is
  if (!/^\d+$/.test(phoneNumber)) return phoneNumber;
  // Add '+' prefix if not already present
  return phoneNumber.startsWith('+') ? phoneNumber : `+${phoneNumber}`;
};
